/**
 * Emit hosts for the `global-template` analytics group — 3 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { type OrganizationTemplateChangedBy } from '@/container/organization/type';
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';
import { type TemplateVisibility } from '@heidi/schema/ml-scribe';

export function trackGlobalTemplateCreated({
  templateId,
  visibility,
  sharedTeams,
}: {
  templateId: string;
  visibility: TemplateVisibility;
  sharedTeams: string[];
}) {
  trackLegacyEvent({
    name: SegmentObjectName.GlobalTemplate,
    props: {
      action: SegmentActionName.GlobalTemplateCreated,
      templateId,
      visibility,
      sharedTeams,
    },
  });
}

export function trackGlobalTemplateDeleted(templateId: string) {
  trackLegacyEvent({
    name: SegmentObjectName.GlobalTemplate,
    props: {
      action: SegmentActionName.GlobalTemplateDeleted,
      templateId,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateDeleted,
    props: {
      template_id: templateId,
      delete_action: 'global_template_deleted',
    },
  });
}

// Global Template
export function trackGlobalTemplateVisibilityChanged({
  templateId,
  changedBy,
  previousVisibility,
  visibility,
  sharedTeams,
}: {
  templateId: string;
  changedBy?: OrganizationTemplateChangedBy;
  previousVisibility: TemplateVisibility;
  visibility: TemplateVisibility;
  sharedTeams?: string[];
}) {
  trackLegacyEvent({
    name: SegmentObjectName.GlobalTemplate,
    props: {
      action: SegmentActionName.GlobalTemplateVisibilityChanged,
      templateId,
      changedBy,
      previousVisibility,
      visibility,
      sharedTeams,
    },
  });

  // V2 — Amplitude + Datadog only
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TemplateUpdated,
    props: {
      template_id: templateId,
      update_action: 'visibility_changed',
      changed_by: changedBy,
      previous_visibility: previousVisibility,
      visibility: visibility,
      shared_teams: sharedTeams,
    },
  });
}
